import React, { useEffect, useState, useMemo } from "react";
import { API_URL } from './config';
import { requestOnboardingTour } from "./OnboardingTour";
import AvailabilityGrid from "./components/AvailabilityGrid";
import { getCurrentResetWeek } from "./lib/time";
import { ruleForRegionName } from "./lib/regionReset";
import { useTimezone } from "./lib/TimezoneContext";
import { getWowClassColor } from "./wowClassColor";
import { characterLogsUrl, useGameLogs } from "./lib/gameLogs";
import {
  FieldLabel,
  MODAL_FIELD_CLS,
  CrossfadeImage,
  modalShellStyle,
  optionIconUrl,
  isLightHex,
  getFactionBannerColor,
} from "./modalChrome";

const CLASS_BG = `${API_URL}/class-backgrounds`;
const CLASS_ART: Record<string, string> = {
  Warrior: `${CLASS_BG}/Warrior.png`,
  Paladin: `${CLASS_BG}/Paladin.png`,
  Hunter: `${CLASS_BG}/hunter.png`,
  Rogue: `${CLASS_BG}/rogue.png`,
  Priest: `${CLASS_BG}/priest.png`,
  "Death Knight": `${CLASS_BG}/deathknight.png`,
  Shaman: `${CLASS_BG}/shaman.png`,
  Mage: `${CLASS_BG}/mage.png`,
  Warlock: `${CLASS_BG}/warlock.png`,
  Monk: `${CLASS_BG}/monk.png`,
  Druid: `${CLASS_BG}/druid.png`,
  "Demon Hunter": `${CLASS_BG}/demonhunter.png`,
  Evoker: `${CLASS_BG}/evoker.png`,
};

function getClassArtUrl(classLabel: string | null | undefined): string | null {
  if (!classLabel) return null;
  const norm = classLabel.trim().toLowerCase().replace(/\s+/g, "");
  const key = Object.keys(CLASS_ART).find((k) => k.toLowerCase().replace(/\s+/g, "") === norm);
  return key ? CLASS_ART[key] : null;
}

function resolveGuildLogoUrl(guild: { logo_url?: string | null; logoUrl?: string | null } | null): string | null {
  const raw = String(guild?.logo_url || guild?.logoUrl || "").trim();
  if (!raw) return null;
  return raw.startsWith("http") ? raw : `${API_URL}${raw}`;
}

const RAID_GREEN = "#34d399";

// --- Local Type Definitions ---

interface Option { id: number; key: string; label: string; metadata: any; }
interface Group { 
  id: number; 
  key: string; 
  label: string; 
  multiSelect: boolean; 
  isRequired: boolean; 
  options: Option[]; 
}

type InvitePageProps = { 
  code: string;
  me: any;
  onJoinSuccess: () => void;
};

// --- AVAILABILITY UTILITIES (Copied from RosterPage.tsx) ---

const TOTAL_AVAIL_SLOTS = 7 * 48; // 336 slots

function bitstringToBoolArray(bits: string | null | undefined): boolean[] {
  const arr = new Array(TOTAL_AVAIL_SLOTS).fill(false);
  if (!bits) return arr;
  const len = Math.min(bits.length, TOTAL_AVAIL_SLOTS);
  for (let i = 0; i < len; i++) {
    arr[i] = bits[i] === "1";
  }
  return arr;
}

function boolArrayToBitstring(arr: boolean[]): string {
  const out: string[] = [];
  const len = Math.min(arr.length, TOTAL_AVAIL_SLOTS);
  for (let i = 0; i < len; i++) out.push(arr[i] ? "1" : "0");
  for (let i = len; i < TOTAL_AVAIL_SLOTS; i++) out.push("0");
  return out.join("");
}

// --- HELPER: URL SLUGIFY ---
function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[']/g, "") // Remove apostrophes (e.g. Kel'Thuzad -> kelthuzad)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// --- MAIN COMPONENT ---

export default function InviteLandingPage({ code, me, onJoinSuccess }: InvitePageProps) {
  const timezone = useTimezone();
  const [loading, setLoading] = useState(true);
  const [guild, setGuild] = useState<any | null>(null);
  const gameLogs = useGameLogs(guild?.game_id);
  const [error, setError] = useState<string | null>(null);
  
  // --- WIZARD STATE ---
  const [wizardStep, setWizardStep] = useState(me ? 1 : 0);
  const [submissionFeedback, setSubmissionFeedback] = useState<string | null>(null);
  
  // Form State
  const [charName, setCharName] = useState("");
  const [wclLink, setWclLink] = useState(""); // <--- NEW: WCL Link State
  const [fields, setFields] = useState<Group[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number | number[]>>({});
  // The week this member's availability lands in follows the guild's region,
  // never the EU default: a US player joining on a Tuesday after reset is
  // already in the next week.
  const inviteWeekStart = useMemo(
    () => getCurrentResetWeek(ruleForRegionName(guild?.region, fields as any)),
    [guild?.region, fields]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [readyToRaid, setReadyToRaid] = useState(false);
  
  // Availability State
  const [accountAvailability, setAccountAvailability] = useState<boolean[]>(() => new Array(TOTAL_AVAIL_SLOTS).fill(false));
  
  // --- LOGIC HELPERS ---

  const isPresetKey = (key: string) => {
      return (key === "realm" && guild?.server_name) || 
             (key === "faction" && guild?.faction) || 
             (key === "region" && guild?.region);
  }
  
  // FIX: Highly targeted validation check for Step 1 fields only
  const areCoreFieldsSelected = useMemo(() => {
      const trimmedName = charName.trim();
      if (!trimmedName) return false;
      if (trimmedName.length < 2 || trimmedName.length > 24) return false;
      
      const classGroup = fields.find(g => g.key === "class");
      const specGroup = fields.find(g => g.key === "spec");

      // 1. Check Class (Mandatory Field)
      if (classGroup && !selectedOptions[classGroup.id]) return false;

      // 2. Check Spec (Mandatory — roles derived from specs on backend)
      if (specGroup) {
          const specVal = selectedOptions[specGroup.id];
          if (specGroup.multiSelect && (!Array.isArray(specVal) || specVal.length === 0)) return false;
          if (!specGroup.multiSelect && !specVal) return false;
      }
      
      return true;
  }, [charName, fields, selectedOptions]);

  // Check for all required fields including realm, faction, region (for non-preset fields)
  const allRequiredFieldsSelected = useMemo(() => {
      if (!areCoreFieldsSelected) return false;
      
      const requiredKeys = ['realm', 'faction', 'region'];
      for (const key of requiredKeys) {
        // Skip if preset by guild
        if (isPresetKey(key)) continue;
        
        const group = fields.find(g => g.key === key);
        if (group) {
          const optionValue = selectedOptions[group.id];
          const hasValue = Array.isArray(optionValue) 
            ? optionValue.length > 0 
            : (optionValue !== undefined && optionValue !== null && Number(optionValue) > 0);
          if (!hasValue) return false;
        }
      }
      return true;
  }, [areCoreFieldsSelected, fields, selectedOptions, isPresetKey]);

  // 2. Logic to filter Roles based on Class
  const classGroup = fields.find(g => g.key === "class");
  const selectedClassId = classGroup ? selectedOptions[classGroup.id] as number : null;
  const selectedClassKey = classGroup?.options.find(o => o.id === selectedClassId)?.key;
  
  // --- AUTO-GENERATE WCL LINK ---
  useEffect(() => {
    if (!guild || !charName) return;

    // Built on the guild's game's own logs site; nothing when it has none.
    const region = (guild.region || "eu").toLowerCase();
    const realmSlug = slugify((guild.server_name || "").replace(/\s*\([^)]*\)\s*/g, "").trim());
    const generated = characterLogsUrl(gameLogs, { region, realmSlug, name: charName });
    if (generated) setWclLink(generated);
  }, [charName, guild, gameLogs]);

  
  // 1. Fetch Guild & Game Fields (Runs once on mount)
  useEffect(() => {
    let isMounted = true;
    const AUTH_BASE_URL = "";
    
    if (!me && wizardStep !== 0) {
        setWizardStep(0);
        return;
    }
    
    (async () => {
      try {
        // Fetch Guild Info
        const gRes = await fetch(`${AUTH_BASE_URL}/guilds/by-code/${code}`);
        if (!gRes.ok) {
          const err = await gRes.json().catch(() => ({ error: "Invite invalid or expired" }));
          throw new Error(err.error || "Invite invalid or expired");
        }
        const gData = await gRes.json();
        
        if (!isMounted) return;
        setGuild(gData);

        // Fetch Game Fields
        const fRes = await fetch(`${AUTH_BASE_URL}/games/${gData.game_id}/fields`);
        const fData = await fRes.json();
        
        if (!isMounted) return;
        setFields(fData);
        
        // --- Availability Fetch ---
        if (me) {
          const availRes = await fetch(`${AUTH_BASE_URL}/me/availability`, { credentials: "include" });
          const availData = await availRes.json();
          if (availData && availData.slots) {
            setAccountAvailability(bitstringToBoolArray(availData.slots));
          }
        }
        
        // Apply Presets (Server/Faction/Region)
        const presets: Record<number, number> = {};
        fData.forEach((group: Group) => {
          const matchVal = 
            (group.key === "realm" && gData.server_name) || 
            (group.key === "faction" && gData.faction) || 
            (group.key === "region" && gData.region);
            
          if (matchVal) {
            const opt = group.options.find(o => o.label.toLowerCase() === matchVal.toLowerCase());
            if (opt) presets[group.id] = opt.id;
          }
        });
        setSelectedOptions(presets);
        
        if (me) setWizardStep(1); 

      } catch (e: any) {
        setError(e.message);
        setWizardStep(0); // Error step forces user to see error/login prompt
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [code, me]);

  const handleLogin = () => {
    sessionStorage.setItem("pending_invite_code", code);
    window.location.href = `${API_URL}/auth/discord`;
  };

  // State for "Add Another Character" flow
  const [showAddAnother, setShowAddAnother] = useState(false);
  const [addedCharacters, setAddedCharacters] = useState<string[]>([]);

  const resetFormForNewCharacter = () => {
    setCharName("");
    setWclLink("");
    // Keep presets (realm, faction, region) but reset class/role
    const groups = fields;
    const newOptions: Record<number, number | number[]> = {};
    groups.forEach((group: Group) => {
      if (isPresetKey(group.key)) {
        // Keep preset values
        if (selectedOptions[group.id]) {
          newOptions[group.id] = selectedOptions[group.id];
        }
      }
      // Clear class, role, and other non-preset fields
    });
    setSelectedOptions(newOptions);
    setWizardStep(1);
    setShowAddAnother(false);
    setSubmissionFeedback(null);
  };

  const handleOnboard = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionFeedback(null);

    // 1. Name validation
    const trimmedName = charName.trim();
    if (!trimmedName) {
      setSubmissionFeedback("Please enter a character name.");
      setIsSubmitting(false);
      setWizardStep(1);
      return;
    }
    if (trimmedName.length < 2) {
      setSubmissionFeedback("Character name must be at least 2 characters.");
      setIsSubmitting(false);
      setWizardStep(1);
      return;
    }
    if (trimmedName.length > 24) {
      setSubmissionFeedback("Character name must be 24 characters or less.");
      setIsSubmitting(false);
      setWizardStep(1);
      return;
    }

    // 2. Core fields validation
    if (!areCoreFieldsSelected) {
      setSubmissionFeedback("Please fill your Character Name, Class, and Spec.");
      setIsSubmitting(false);
      setWizardStep(1); 
        return;
    }

    // 3. All required fields validation (realm, faction, region if not preset)
    if (!allRequiredFieldsSelected) {
        setSubmissionFeedback("Please select all required fields (Realm, Faction, Region).");
        setIsSubmitting(false);
        setWizardStep(1); 
        return;
    }

    try {
      // --- 2. SAVE AVAILABILITY (Non-Transactional Update) ---
      const currentAvailabilityBits = boolArrayToBitstring(accountAvailability);
      const hasAvailabilityData = currentAvailabilityBits.includes("1");

      if (hasAvailabilityData) {
        // Save to current week AND next week - a member joining now is available
        // this week, and next week starts pre-filled + confirmed with the same
        // times so they don't show up as "not confirmed" on day one.
        const availRes = await fetch("/me/availability", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
                slots: currentAvailabilityBits,
                weekStart: inviteWeekStart,
                applyToNextWeek: true,
                confirm: true
            }),
        });
        if (!availRes.ok) {
            console.warn("Failed to save availability, proceeding with character onboarding.");
        }
    }

      // --- 3. Character Creation (Atomic Logic) ---
      const payloadOptions: Record<number, number | number[]> = {};
      for (const [gidStr, val] of Object.entries(selectedOptions)) {
        const gid = Number(gidStr);
        if (!val) continue;
        if (Array.isArray(val)) { if (val.length > 0) payloadOptions[gid] = val; }
        else if (typeof val === 'number' && val > 0) payloadOptions[gid] = val;
      }

      const res = await fetch("/guilds/onboard", { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        credentials: "include", 
        body: JSON.stringify({
          inviteCode: code,
          gameId: guild.game_id,
          name: trimmedName,
          options: payloadOptions,
          // --- NEW: Send WCL Link in metadata ---
          metadata: {
            wcl_url: wclLink
          }
        })
      });

      if (!res.ok) {
        const j = await res.json().catch(() => ({ error: "Server error" }));
        if (j.message) {
          throw new Error(j.message);
        } else if (j.error === 'missing_required_fields') {
          throw new Error(`Missing required fields: ${j.fields?.join(', ') || 'unknown'}`);
        }
        throw new Error(j.error || "Failed to join guild.");
      }
      
      sessionStorage.removeItem("pending_invite_code");
      
      // Track added character and show "Add Another" option
      setAddedCharacters(prev => [...prev, trimmedName]);
      setShowAddAnother(true);
      setWizardStep(4); // New step for success + add another

      // Queue the one-time setup tour. The invite wizard already covers
      // availability, so this waits as a pill and engages on the Roster to
      // introduce the remaining features (sync, BiS, squad).
      requestOnboardingTour(trimmedName);

    } catch (e: any) {
      const msg = e.message === 'invalid_role_for_class' 
        ? "Class/Role mismatch. Please correct your selections." 
        : e.message.startsWith('Join failed:') ? e.message : `Join failed: ${e.message}`;
      setSubmissionFeedback(msg);
      setWizardStep(1); 
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // --- RENDER LOGIC ---

  if (loading) return <div className="p-10 text-center text-white/40 animate-pulse">Loading invite...</div>;
  if (error && wizardStep === 0) return <div className="p-10 text-center text-red-400 font-bold">{error}</div>;

  const selectedClass = classGroup?.options.find((o) => o.id === selectedClassId) || null;
  const classLabel = selectedClass?.label || null;
  const hasClass = Boolean(classLabel);
  const splashUrl = getClassArtUrl(classLabel);
  const guildLogoUrl = resolveGuildLogoUrl(guild);
  const accent = hasClass
    ? getWowClassColor(classLabel)
    : guild?.faction
      ? getFactionBannerColor(guild.faction)
      : "#A3A3A3";
  const shellAccent = hasClass || guild?.faction ? accent : "#27272a";
  const ctaColor = isLightHex(accent) ? "#111" : "#fff";
  const headerTitle = charName.trim() || (guild ? `Join ${guild.name}` : "Join Guild");
  const headerSub = guild
    ? [guild.gameName, guild.server_name || "Any Realm", guild.faction || "Any Faction"].filter(Boolean).join(" · ")
    : "Sign in to continue.";

  const wizardContent = (
    <form id="onboard-form" onSubmit={handleOnboard} className="w-full">
      {wizardStep < 4 && (
        <div className="flex justify-between items-center px-6 pt-5 pb-3 border-b border-white/10">
          <div className="text-[10px] font-bold uppercase tracking-widest text-white/50">
            {addedCharacters.length > 0
              ? `Step ${wizardStep === 1 ? 1 : 2} of 2`
              : `Step ${wizardStep} of 3`}
          </div>
          <div className="flex items-center gap-1">
            {addedCharacters.length > 0
              ? [1, 2].map((s) => (
                  <div
                    key={s}
                    className="char-modal-tone h-2 rounded-full"
                    style={{
                      width:
                        (s === 1 && wizardStep >= 1) || (s === 2 && wizardStep === 3) ? 32 : 16,
                      backgroundColor:
                        (s === 1 && wizardStep >= 1) || (s === 2 && wizardStep === 3)
                          ? accent
                          : "rgba(255,255,255,0.1)",
                    }}
                  />
                ))
              : [1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className="char-modal-tone h-2 rounded-full"
                    style={{
                      width: s <= wizardStep ? 32 : 16,
                      backgroundColor: s <= wizardStep ? accent : "rgba(255,255,255,0.1)",
                    }}
                  />
                ))}
          </div>
        </div>
      )}

      <div className={`p-6 space-y-5 ${wizardStep === 2 ? "overflow-x-auto" : ""}`}>
        {wizardStep === 1 && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2">
            <div className="space-y-1.5">
              <FieldLabel>Character Name</FieldLabel>
              <input
                value={charName}
                onChange={(e) => setCharName(e.target.value)}
                className={`${MODAL_FIELD_CLS} text-base font-semibold`}
                placeholder="Name"
                minLength={2}
                maxLength={24}
                required
              />
            </div>

            {wclLink ? (
              <div
                className="char-modal-tone space-y-2 p-3 rounded-xl border"
                style={{
                  borderColor: "rgba(244, 140, 64, 0.45)",
                  backgroundColor: "rgba(244, 140, 64, 0.1)",
                  boxShadow: "0 0 18px rgba(244, 140, 64, 0.12)",
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border"
                    style={{ backgroundColor: "rgba(244, 140, 64, 0.2)", borderColor: "rgba(244, 140, 64, 0.4)" }}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#f48c40" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 19V9m5 10V5m5 14v-8m5 8V3" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#f48c40" }}>
                      Warcraft Logs
                    </div>
                    <div className="text-[11px] text-white/45 truncate mt-0.5">Edit if your logs live elsewhere.</div>
                  </div>
                  <a
                    href={wclLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0"
                    title="Open Warcraft Logs"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#f48c40" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                  </a>
                </div>
                <input
                  value={wclLink}
                  onChange={(e) => setWclLink(e.target.value)}
                  className={`${MODAL_FIELD_CLS} font-mono text-xs`}
                  placeholder="https://classic.warcraftlogs.com/character/..."
                />
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.03]">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border border-white/10 bg-black/40">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 19V9m5 10V5m5 14v-8m5 8V3" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-white/35">Warcraft Logs</div>
                  <div className="text-[11px] text-white/25 truncate mt-0.5">Fills in once a name is set</div>
                </div>
              </div>
            )}

            {fields.filter((g) => g.key === "class").map((group) => (
              <div key={group.id} className="space-y-2">
                <FieldLabel>
                  {group.label}
                  {group.isRequired ? <span className="text-red-400"> *</span> : null}
                </FieldLabel>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {group.options.map((opt) => {
                    const color = getWowClassColor(opt.label);
                    const selected = selectedOptions[group.id] === opt.id;
                    const icon = optionIconUrl(opt, opt.label);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        title={opt.label}
                        onClick={() => {
                          const val = opt.id;
                          const specGroup = fields.find((g) => g.key === "spec");
                          if (specGroup) {
                            setSelectedOptions((p) => {
                              const n = { ...p, [group.id]: val };
                              delete n[specGroup.id];
                              return n;
                            });
                          } else {
                            setSelectedOptions((p) => ({ ...p, [group.id]: val }));
                          }
                        }}
                        className="char-modal-tone relative aspect-square rounded-xl border overflow-hidden bg-black/40"
                        style={
                          selected
                            ? { borderColor: color, boxShadow: `0 0 16px ${color}66` }
                            : { borderColor: "rgba(255,255,255,0.1)" }
                        }
                      >
                        {icon ? (
                          <img src={icon} alt="" className="absolute inset-0 h-full w-full object-cover" />
                        ) : (
                          <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold" style={{ color }}>
                            {opt.label.charAt(0)}
                          </span>
                        )}
                        <span className="absolute inset-x-0 bottom-0 pt-4 pb-1 px-0.5 bg-gradient-to-t from-black/90 via-black/55 to-transparent">
                          <span
                            className="block text-[10px] font-medium leading-tight text-center"
                            style={{
                              color: selected ? color : "rgba(255,255,255,0.82)",
                              textShadow: "0 1px 4px rgba(0,0,0,0.9)",
                            }}
                          >
                            {opt.label}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {fields.filter((g) => g.key === "spec").map((group) => {
              if (!selectedClassKey) {
                return (
                  <div key={group.id} className="space-y-2">
                    <FieldLabel>
                      {group.label}
                      {group.isRequired ? <span className="text-red-400"> *</span> : null}
                    </FieldLabel>
                    <div className="text-xs text-white/30">Select a class to see specs.</div>
                  </div>
                );
              }
              const visibleOptions = group.options.filter((opt) => {
                const allowed = opt.metadata?.allowed_class_keys || opt.metadata?.allowedClassKeys;
                return !allowed || allowed.includes(selectedClassKey);
              });

              return (
                <div key={group.id} className="space-y-2">
                  <FieldLabel>
                    {group.label}
                    {group.isRequired ? <span className="text-red-400"> *</span> : null}
                  </FieldLabel>
                  {visibleOptions.length === 0 ? (
                    <div className="text-xs text-white/30">No specs available for this class.</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {visibleOptions.map((opt) => {
                        const isSelected = Array.isArray(selectedOptions[group.id])
                          ? (selectedOptions[group.id] as number[]).includes(opt.id)
                          : selectedOptions[group.id] === opt.id;
                        const icon = optionIconUrl(opt, classLabel);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => {
                              const prev = (selectedOptions[group.id] as number[]) || [];
                              const next = isSelected ? prev.filter((x) => x !== opt.id) : [...prev, opt.id];
                              setSelectedOptions((p) => ({ ...p, [group.id]: next }));
                            }}
                            className="char-modal-tone flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border"
                            style={
                              isSelected
                                ? { borderColor: accent, backgroundColor: `${accent}33`, color: "#fff" }
                                : {
                                    backgroundColor: "rgba(0,0,0,0.4)",
                                    borderColor: "rgba(255,255,255,0.1)",
                                    color: "rgba(255,255,255,0.6)",
                                  }
                            }
                          >
                            {icon && <img src={icon} alt="" className="w-4 h-4 rounded" />}
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {(() => {
              const raidInterestGroup = fields.find((g) => g.key === "raid_interest" || g.key === "raid_preferences");
              if (!raidInterestGroup) return null;

              return (
                <div className="space-y-3 pt-3 border-t border-white/10 mt-1">
                  <label
                    className="relative flex items-center gap-3 cursor-pointer group/rtr"
                    onClick={(e) => {
                      e.preventDefault();
                      if (readyToRaid) {
                        setReadyToRaid(false);
                        setSelectedOptions((p) => {
                          const n = { ...p };
                          delete n[raidInterestGroup.id];
                          return n;
                        });
                      } else {
                        setReadyToRaid(true);
                      }
                    }}
                  >
                    <div
                      className="char-modal-tone w-5 h-5 rounded border flex items-center justify-center flex-shrink-0"
                      style={
                        readyToRaid
                          ? { backgroundColor: RAID_GREEN, borderColor: RAID_GREEN }
                          : { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.28)" }
                      }
                    >
                      {readyToRaid && (
                        <svg className="w-3 h-3" fill="none" stroke="#052e16" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span
                      className={
                        readyToRaid
                          ? "ready-raid-title"
                          : "char-modal-tone text-xs font-bold uppercase tracking-widest text-white/80"
                      }
                    >
                      Ready to Raid
                    </span>
                    <div className="absolute left-8 top-full mt-2 px-3 py-2 bg-black/95 border border-white/20 rounded-lg text-xs text-white/80 w-64 opacity-0 pointer-events-none group-hover/rtr:opacity-100 z-50 shadow-xl normal-case tracking-normal font-normal leading-relaxed">
                      Mark this character as raid-ready and select which raids you are interested in.
                    </div>
                  </label>

                  {readyToRaid && (
                    <div className="flex flex-wrap gap-2 pl-8">
                      {raidInterestGroup.options.map((opt) => {
                        const isSelected = Array.isArray(selectedOptions[raidInterestGroup.id])
                          ? (selectedOptions[raidInterestGroup.id] as number[]).includes(opt.id)
                          : selectedOptions[raidInterestGroup.id] === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => {
                              const prev = (selectedOptions[raidInterestGroup.id] as number[]) || [];
                              const next = isSelected ? prev.filter((x) => x !== opt.id) : [...prev, opt.id];
                              setSelectedOptions((p) => ({ ...p, [raidInterestGroup.id]: next }));
                            }}
                            className="char-modal-tone px-3 py-1.5 rounded-full text-xs font-medium border"
                            style={
                              isSelected
                                ? {
                                    backgroundColor: "rgba(16, 185, 129, 0.18)",
                                    borderColor: RAID_GREEN,
                                    color: "#a7f3d0",
                                  }
                                : {
                                    backgroundColor: "rgba(0,0,0,0.4)",
                                    borderColor: "rgba(255,255,255,0.1)",
                                    color: "rgba(255,255,255,0.45)",
                                  }
                            }
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            <p className="text-[10px] text-white/35 pt-1">
              Realm, faction, and region are set by the guild and cannot be changed here.
            </p>
          </div>
        )}

        {wizardStep === 2 && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
            <div>
              <h3 className="text-lg font-bold text-white">Your Schedule</h3>
              <p className="text-sm text-white/55 mt-1">
                Mark the times you are consistently available to raid. This helps the raid leaders plan.
              </p>
              <p className="text-xs text-white/40 mt-2">
                We confirm these times for this week and next week automatically, so you show up in the schedule right away. You can change either week later on your roster.
              </p>
            </div>
            <div className="bg-black/40 p-4 rounded-xl border border-white/10">
              <AvailabilityGrid
                week={accountAvailability}
                weekStartIso={inviteWeekStart}
                userTimezone={timezone}
                displayDayOrderLocalMon0={[0, 1, 2, 3, 4, 5, 6]}
                daysDisplay={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]}
                compact
                onChange={setAccountAvailability}
              />
            </div>
          </div>
        )}

        {wizardStep === 3 && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2">
            <h3 className="text-lg font-bold text-white">Review</h3>
            <div
              className="char-modal-tone flex items-center gap-3 p-4 rounded-xl border"
              style={{ borderColor: `${accent}44`, backgroundColor: `${accent}14` }}
            >
              {guildLogoUrl ? (
                <div
                  className="relative w-12 h-12 rounded-xl overflow-hidden border shrink-0 bg-black/40"
                  style={{ borderColor: `${accent}55` }}
                >
                  <img src={guildLogoUrl} alt="" className="w-full h-full object-cover" />
                </div>
              ) : null}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold" style={{ color: accent }}>
                  {charName}
                </div>
                <div className="text-xs text-white/60 mt-0.5">
                  Ready to join <span className="font-bold text-white">{guild.name}</span>
                </div>
                {wclLink && <div className="text-[10px] text-white/40 mt-1 truncate">{wclLink.replace(/^https?:\/\//, "")}</div>}
              </div>
            </div>
            <p className="text-sm text-white/60">This will create your character and join the guild.</p>
          </div>
        )}

        {wizardStep === 4 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Welcome to {guild?.name}!</h3>
              <p className="text-sm text-white/60 mt-2">
                {addedCharacters.length === 1
                  ? `${addedCharacters[0]} has been added to the roster.`
                  : `${addedCharacters.length} characters added: ${addedCharacters.join(", ")}`}
              </p>
            </div>
            <div className="flex flex-col gap-3 pt-2">
              <button
                type="button"
                onClick={resetFormForNewCharacter}
                className="char-modal-tone w-full py-3 font-bold text-xs uppercase tracking-widest rounded-xl"
                style={{ backgroundColor: accent, color: ctaColor }}
              >
                Add Another Character
              </button>
              <button
                type="button"
                onClick={onJoinSuccess}
                className="w-full py-3 font-bold text-xs uppercase tracking-widest rounded-xl bg-white/10 text-white hover:bg-white/15"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {wizardStep < 4 && (
          <div className="pt-5 border-t border-white/10 mt-2">
            {submissionFeedback && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm">
                {submissionFeedback}
              </div>
            )}
            <div className="flex justify-between items-center gap-3">
              <button
                type="button"
                onClick={() => setWizardStep((s) => Math.max(1, s - 1))}
                disabled={wizardStep === 1 || isSubmitting}
                className="px-4 py-2 text-sm text-white/50 hover:text-white disabled:opacity-30"
              >
                Back
              </button>
              {wizardStep < 3 && (
                <button
                  type="button"
                  onClick={() => {
                    if (wizardStep === 1 && !areCoreFieldsSelected) {
                      setSubmissionFeedback("Please fill your Character Name, Class, and Spec.");
                      return;
                    }
                    setSubmissionFeedback(null);
                    if (wizardStep === 1 && addedCharacters.length > 0) {
                      setWizardStep(3);
                    } else {
                      setWizardStep((s) => s + 1);
                    }
                  }}
                  disabled={isSubmitting}
                  className="char-modal-tone px-6 py-2.5 font-bold text-xs uppercase tracking-widest rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: accent, color: ctaColor }}
                >
                  Next
                </button>
              )}
              {wizardStep === 3 && (
                <button
                  type="submit"
                  form="onboard-form"
                  disabled={isSubmitting}
                  className="char-modal-tone flex items-center justify-center gap-2 px-6 py-2.5 font-bold text-xs uppercase tracking-widest rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: accent, color: ctaColor }}
                >
                  {isSubmitting && (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isSubmitting ? "Joining..." : "Join Guild"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </form>
  );

  return (
    <div
      className={`flex flex-col items-center justify-center min-h-[80vh] w-full mx-auto p-4 ${
        wizardStep === 2 ? "max-w-screen-xl" : "max-w-xl"
      }`}
    >
      <div
        className="char-modal-shell w-full bg-[#09090b] border rounded-xl overflow-hidden"
        style={modalShellStyle(shellAccent)}
      >
        <div className="relative h-48 shrink-0 overflow-hidden">
          <div className="char-modal-splash-idle absolute inset-0 z-0" />
          <CrossfadeImage
            src={splashUrl ? null : guildLogoUrl}
            className="absolute inset-0 z-0 h-full w-full"
            imgClassName="absolute inset-0 h-full w-full object-cover object-center scale-125 blur-md"
          />
          <CrossfadeImage
            src={splashUrl}
            className="absolute inset-0 z-0 h-full w-full"
            imgClassName="absolute inset-0 h-full w-full object-cover [object-position:center_18%] scale-110"
          />
          <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-[#09090b] via-[#09090b]/35 to-black/10" />
          <div className="relative z-[2] h-full flex items-end gap-3 p-5">
            <div
              className="char-modal-tone relative w-14 h-14 rounded-xl border overflow-hidden shrink-0 bg-black/40 flex items-center justify-center"
              style={
                guildLogoUrl || hasClass
                  ? { borderColor: `${accent}66`, boxShadow: `0 0 18px ${accent}44` }
                  : { borderColor: "rgba(255,255,255,0.1)", boxShadow: "none" }
              }
            >
              {guildLogoUrl ? (
                <CrossfadeImage src={guildLogoUrl} className="absolute inset-0 h-full w-full" />
              ) : null}
            </div>
            <div className="min-w-0 flex-1 pb-0.5">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">
                {guild ? `Join ${guild.name}` : "Join Guild"}
              </div>
              <h1
                className="char-modal-tone text-xl font-bold truncate leading-tight"
                style={{
                  color: hasClass ? accent : "rgba(255,255,255,0.92)",
                  textShadow: hasClass ? "0 1px 10px rgba(0,0,0,0.85)" : "none",
                }}
              >
                {headerTitle}
              </h1>
              <div
                className="text-xs truncate mt-0.5"
                style={{
                  color: hasClass ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)",
                  textShadow: hasClass ? "0 1px 8px rgba(0,0,0,0.8)" : "none",
                }}
              >
                {headerSub}
              </div>
            </div>
          </div>
        </div>

        {me && wizardStep > 0 ? (
          wizardContent
        ) : (
          <div className="p-6 text-center space-y-4">
            <p className="text-white/60 text-sm">Log in with Discord to create a character and join this guild.</p>
            <button
              onClick={handleLogin}
              className="px-8 py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold rounded-xl transition-all"
            >
              Log in with Discord
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
